import { Component, computed, inject, input, signal, effect, OnDestroy, output } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { APIService } from 'src/app/services/APIService/api.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { Options } from '@angular-slider/ngx-slider';

export interface CamImageResponse {
  data: ImageFrame[];
  path: string;
  isDual: boolean;
}

export interface ImageFrame {
  SampleIndex: number;
  Km: number;
  File: string;
  Key: string;
}

@Component({
  selector: 'app-streckenbilder',
  standalone: true,
  imports: [CommonModule, ClarityModule, FormsModule, NgxSliderModule],
  templateUrl: './streckenbilder.component.html',
  styleUrl: './streckenbilder.component.scss',
})
export class StreckenbilderComponent implements OnDestroy {
  protected authService = inject(AuthService);
  private mrTranslate = inject(MrTranslatePipe);

  public slideroptions: Options = {
    floor: 0,
    ceil: 100,
  };

  public readonly opruefid = input.required<number>();
  public readonly pos = output<number>();
  private readonly apiService = inject(APIService);

  // -------------------------
  // API RESOURCE
  // -------------------------
  private readonly bilderResource = rxResource<CamImageResponse, number>({
    params: this.opruefid,
    stream: ({ params }) => this.apiService.getAnlagenCamBilder(params),
  });

  // -------------------------
  // DERIVED DATA (READ ONLY)
  // -------------------------
  protected readonly frames = computed<ImageFrame[]>(() => this.bilderResource.value()?.data ?? []);
  protected readonly dualSystem = computed<boolean>(() => this.bilderResource.value()?.isDual ?? false);

  protected readonly imagesLoading = this.bilderResource.isLoading;

  // -------------------------
  // UI STATE
  // -------------------------
  protected currentIndex = signal(0);
  protected currentImage = signal('');
  protected currentImage_R = signal('');
  protected currentKm = signal<number>(0);

  // -------------------------
  // INTERNAL STATE
  // -------------------------
  private intervalId?: number;
  private lastObjectUrl: string | null = null;
  private lastObjectUrl_R: string | null = null;

  constructor() {
    // Auto-load first frame when data arrives
    effect(() => {
      const frames = this.frames();

      if (frames.length === 0) return;

      this.slideroptions = {
        floor: 0,
        translate: this.getPosition,
        ceil: frames.length - 1,
        animate: false,
      };

      // only initialize once
      if (this.currentIndex() === 0 && !this.currentImage()) {
        this.setFrame(0);
      }
    });
  }

  getPosition = val => {
    if (val !== undefined && !Number.isNaN(val)) {
      const frames = this.frames();
      const frame = frames[val];

      if (frame) {
        let m = frame.Km * 1000;
        return +Number(m.toFixed(1));
      }
      return val;
    }
    return '0';
  };

  // -------------------------
  // FRAME LOADING
  // -------------------------
  setFrame(index: number) {
    const frames = this.frames();
    const frame = frames[index];

    if (!frame) return;

    this.currentIndex.set(index);
    this.currentKm.set(frame.Km);
    this.pos.emit(this.currentKm());

    if (this.dualSystem()) {

      this.apiService.getAnlagenCamBild(this.opruefid(), frame.Key).subscribe(blob_L => {
        this.apiService.getAnlagenCamBild(this.opruefid(), frame.Key,true).subscribe(blob_R => {
          const url_L = URL.createObjectURL(blob_L);
          const url_R = URL.createObjectURL(blob_R);

          // cleanup previous image (IMPORTANT)
          if (this.lastObjectUrl) {
            URL.revokeObjectURL(this.lastObjectUrl);
            URL.revokeObjectURL(this.lastObjectUrl_R);
          }

          this.lastObjectUrl = url_L;
          this.lastObjectUrl_R = url_R;
          this.currentImage_R.set(url_L); //Tauschen, wird andersrum geladen
          this.currentImage.set(url_R);
        });

      });
    } else {
      this.apiService.getAnlagenCamBild(this.opruefid(), frame.Key).subscribe(blob => {
        const url = URL.createObjectURL(blob);

        // cleanup previous image (IMPORTANT)
        if (this.lastObjectUrl) {
          URL.revokeObjectURL(this.lastObjectUrl);
        }

        this.lastObjectUrl = url;
        this.currentImage.set(url);
      });
    }

  }

  // -------------------------
  // SLIDER
  // -------------------------
  onSliderChange(value: number | string) {
    this.setFrame(Number(value));
  }

  // -------------------------
  // PLAYBACK
  // -------------------------
  play() {
    this.stop(); // prevent multiple intervals

    this.intervalId = window.setInterval(() => {
      const idx = this.currentIndex();
      const max = this.frames().length - 1;

      if (idx < max) {
        this.setFrame(idx + 1);
      } else {
        this.stop();
      }
    }, 100);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  // -------------------------
  // CLEANUP
  // -------------------------
  ngOnDestroy() {
    this.stop();
    this.pos.emit(-98);
    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
    }
  }
}
